import {
  useReducer,
  createContext,
  useCallback,
  useEffect,
  useContext,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { useLocale } from 'hooks/useLocale';
import { useGetApplications } from 'lendingApi/applications.hooks';
import {
  generateDateMonthsPrevious,
  getDateRangeNumber,
  getIncludePartnerChannel,
} from './Dashboard.context.helpers';
import reducer from './Dashboard.reducer';
import { TDashboardState } from './Dashboard.types';
import { LOCAL_STORAGE_KEY } from './DashboardHeader/SearchBar/RecentSearches/RecentSearches';

const DashboardContext = createContext<
  | {
      state: TDashboardState;
      isLoading?: boolean;
      isError: boolean;
      redirectionId?: string;
      setRedirectionId: (id?: string) => void;
      isSettingsOpen: boolean;
      setIsSettingsOpen: Dispatch<SetStateAction<boolean>>;
      searchApplication: (searchString?: string) => void;
      searchApplicationDateRange: (dateFrom: string | null) => void;
      searchApplicationPartnerChannelToggle: (
        includePartnerChannel: boolean,
      ) => void;
    }
  | undefined
>(undefined);

export const DashboardProvider = (props: any) => {
  const initialState = {
    dateFrom: generateDateMonthsPrevious(getDateRangeNumber()),
    includePartnerChannel: getIncludePartnerChannel(),
    statusList: [],
    searchString: undefined,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const [redirectionId, setRedirectionId] = useState<string>();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { locale } = useLocale();
  const {
    data: applications,
    isError: isApplicationError,
    isLoading: isApplicationLoading,
    status,
  } = useGetApplications(
    state.searchString,
    state.dateFrom,
    state.includePartnerChannel,
  );

  const searchApplication = useCallback(async (searchString?: string) => {
    const currentSearches = localStorage.getItem(LOCAL_STORAGE_KEY);
    const currentSearchesParsed =
      currentSearches && (await JSON.parse(currentSearches));

    if (currentSearchesParsed) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify([...currentSearchesParsed, searchString]),
      );
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([searchString]));
    }

    dispatch({ type: 'searchApplication', searchString });
  }, []);

  const searchApplicationDateRange = (dateFrom: string | null) => {
    dispatch({ type: 'searchApplicationDateRange', dateFrom });
  };

  const searchApplicationPartnerChannelToggle = (
    includePartnerChannel: boolean,
  ) => {
    dispatch({
      type: 'searchApplicationPartnerChannelToggle',
      includePartnerChannel,
    });
  };

  const isLoading = isApplicationLoading || status === 'pending';

  const getData = useCallback(() => {
    dispatch({
      type: 'fetchedApplications',
      applications,
      locale,
    });
  }, [applications, locale]);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <DashboardContext.Provider
      value={{
        state,
        isLoading,
        isError: isApplicationError,
        redirectionId,
        setRedirectionId,
        isSettingsOpen,
        setIsSettingsOpen,
        searchApplication,
        searchApplicationDateRange,
        searchApplicationPartnerChannelToggle,
      }}
      {...props}
    />
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within');
  }
  return context;
};
